import { useBlocker } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { isFormEnabled } from '@/pages/approval/formGroups';
import type { ApprovalForm } from '@/pages/approval/useApproval';

/**
 * 아직 열지 않은 서식으로 가는 길을 <b>떠나기 전에</b> 막는다 (사용자 지시 2026-10-06 ~ 10-07).
 *
 * <p>마이페이지(자격증·검진·콘도·경조)·승진·성과급·잡자재·사내지침의 버튼과 AI 링크는 서식을 채워
 * {@code /approval/draft?formKey=…} 로 보낸다. 처음에는 기안하기 화면에서 「준비 중」 을 띄우고 뒤로
 * 돌려보냈는데, 탭을 화면 안에만 쥔 곳(마이페이지)은 돌아오면 첫 탭으로 튕겼다 —
 * 「사용자가 사용중이던 화면에서 그대로 유지되어야지」. 그래서 이동 자체를 붙잡고 그 자리에서 알린다.
 *
 * <p>서식 하나하나의 출발점을 고치지 않고 라우터에서 한 번에 거른다 — 출발점은 열 곳이 넘고 앞으로도
 * 는다. 이미 올라간 문서를 고치러 가는 길({@code ?id=})은 막지 않는다. 주소를 직접 치고 들어온
 * 경우는 라우터 이동이 아니라 여기 걸리지 않고, 기안하기 화면이 같은 말을 띄운다.
 *
 * <p>열렸는지는 서식 응답의 {@code draftOpen}(V467)으로 본다. 여기서 새로 조회하지 않는다 — 이 가드는
 * 로그인한 전원의 모든 화면에 얹혀 있어, 조회를 걸면 결재를 쓰지 않는 사람에게도 요청이 나간다.
 * 캐시에 목록이 아직 없으면 막지 않고, 기안하기 화면이 목록을 받아 같은 말을 띄운다.
 */
export function NotReadyFormGuard() {
  const queryClient = useQueryClient();
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (nextLocation.pathname !== '/approval/draft') return false;
    // 기안하기 안에서 서식을 바꾸는 이동은 그 화면이 회색으로 막는다.
    if (currentLocation.pathname === '/approval/draft') return false;
    const params = new URLSearchParams(nextLocation.search);
    if (params.get('id')) return false;
    const formKey = params.get('formKey');
    if (!formKey) return false;
    const forms = queryClient.getQueryData<ApprovalForm[]>(['approval', 'forms']);
    const form = forms?.find((f) => f.formKey === formKey);
    return !!form && !isFormEnabled(form);
  });

  if (blocker.state !== 'blocked') return null;
  const stay = () => blocker.reset?.();
  return (
    <Modal
      title="준비 중"
      onClose={stay}
      maxWidth="360px"
      footer={<button className="btn-primary" onClick={stay}>확인</button>}
    >
      <p style={{ fontSize: '14px' }}>준비 중입니다. 곧 오픈됩니다!</p>
    </Modal>
  );
}
